'use client';

import { useState, useEffect } from 'react';
import {
    getProductCard,
    PRODUCT_GROUP_SOURCE_TYPE,
    PRODUCT_GROUP_SORTING_TYPE,
} from 'apis/product';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type LayoutNodeStyle } from 'shop-ui/types';

interface UseProductGroupProps {
    styleMap: LayoutNodeStyle;
    isBuyer?: boolean;
}

export const useProductGroup = (props: UseProductGroupProps) => {
    const { styleMap, isBuyer = false } = props;

    const [productCards, setProductCards] = useState<ProductCard[]>([]);

    const [isLoading, setIsLoading] = useState(isBuyer);

    useEffect(() => {
        const type =
            (styleMap?.[
                FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE_TYPE
            ] as PRODUCT_GROUP_SOURCE_TYPE) || '';

        let query: string | null = null;

        if (
            type === PRODUCT_GROUP_SOURCE_TYPE.SELECTED &&
            styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE]
        ) {
            query = (
                (styleMap[
                    FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE
                ] as unknown as string[]) || []
            ).join(',');
        }

        if (
            type === PRODUCT_GROUP_SOURCE_TYPE.RECOMMEND &&
            styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE]
        ) {
            query = (
                (styleMap[
                    FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE
                ] as unknown as string[]) || []
            ).join(',');
        }

        if (
            type === PRODUCT_GROUP_SOURCE_TYPE.KEYWORD &&
            styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE]
        ) {
            query = (styleMap[FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE] as string) || null;
        }

        if (
            type === PRODUCT_GROUP_SOURCE_TYPE.CATEGORY &&
            styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE]
        ) {
            query = (
                (styleMap[
                    FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE
                ] as unknown as string[]) || []
            ).reduceRight<string | null>((acc, current) => acc || current, null);
        }

        const extraOptions = {
            asc: false,
        };

        const orderBy =
            (styleMap?.[
                FEATURE_FLAG_KEYS.PRODUCT_GROUP_SOURCE_SORTING_TYPE
            ] as PRODUCT_GROUP_SORTING_TYPE) || null;

        const perPage =
            (styleMap?.[FEATURE_FLAG_KEYS.PRODUCT_GROUP_CARD_DISPLAY_AMOUNT] as number) ||
            1;

        (async () => {
            setIsLoading(true);

            const resp = await getProductCard({
                type,
                query,
                order_by: orderBy,
                per_page: perPage,
                ...extraOptions,
            });

            if (resp?.data) {
                setProductCards(resp.data);
            }

            setIsLoading(false);
        })();
    }, [styleMap]);

    return {
        productCards,
        isLoading,
    };
};
